/**
 * Concrete commissioned themes for Good-time Casino. These live in the
 * app (not the engine) because they import real PNG URLs from
 * {@code apps/mobile/src/assets/reel/{theme}/}; the engine package
 * stays pure and has no awareness of where the host stores assets.
 *
 * <p>Five themes correspond 1:1 to the five world-map biomes:
 * <ul>
 *   <li>{@link FOREST_THEME}     — Whispering Woods (Greendale Kingdom)
 *   <li>{@link UNDERWATER_THEME} — Stormy Seas (Saltspray Cove)
 *   <li>{@link FROST_THEME}      — Crystal Peak (Frostpeak Range)
 *   <li>{@link FRONTIER_THEME}   — Sunset Mesa (Dustwood Frontier)
 *   <li>{@link NEON_THEME}       - Neon Heights (named after its world)
 * </ul>
 *
 * <p>Each theme reuses the engine's slot symbol-id schema (cherry,
 * lemon, orange, bell, star, seven, gem, scatter, money) so payline
 * maths and bonus triggers stay portable across themes — the visible
 * art changes; the physics don't.
 */
import {DEFAULT_THEME, MIDNIGHT_THEME, SUNSET_THEME, type SlotSymbolDef, type ThemeAssetEntry, ThemeRegistry, type WinTier,} from '@goodtime/engine';
import { ARTEFACTS, BIOME_BY_THEME_ID, type BiomeId } from '@goodtime/shared';
import { AMBIENT_ART, AMBIENT_LIFE } from './ambientLife';
// Reel-Race special-symbol art (reused from forest for every skin — see
// REEL_RACE_SYMBOLS / REEL_RACE_TEXTURES below).
import reelRaceBonusUrl from '../assets/reel/forest/bonus.png';
import reelRaceItemDropUrl from '../assets/reel/forest/item_drop.png';
import bigWinUrl from '../assets/type/big_win.png';
import jackpotUrl from '../assets/type/jackpot.png';
import megaWinUrl from '../assets/type/mega_win.png';
import superMegaWinUrl from '../assets/type/super_mega_win.png';
import winUrl from '../assets/type/win.png';
import coinIconUrl from '../assets/icon/coin.png';
import coinLockedIconUrl from '../assets/icon/coin-locked.png';
import infoIconUrl from '../assets/icon/info.png';
import spinIconUrl from '../assets/icon/spin.png';
import gemIconUrl from '../assets/icon/gem.png';
// Commissioned button reskin assets (asset brief §7+). State-aware
// where applicable — the engine widgets swap the active texture on
// idle/pressed/disabled.
import spinIdleUrl from '../assets/ui_button/spin_idle.png';
import spinPressedUrl from '../assets/ui_button/spin_pressed.png';
import spinDisabledUrl from '../assets/ui_button/spin_disabled.png';
import gambleIdleUrl from '../assets/ui_button/gamble_idle.png';
import gambleTitleUrl from '../assets/ui_button/gamble_title.png';
// Commissioned gamble-flip cards (2026-06-04 brief). The GambleUI's
// previously-programmatic Graphics card swaps to a sprite of one of
// these PNGs on reveal. Painterly red heart / black spade faces;
// classic playing-card proportions inside the 1024×1024 commission frame.
import gambleCardRedUrl from '../assets/card/face_red_gamble.png';
import gambleCardBlackUrl from '../assets/card/face_black_gamble.png';
import infoIdleUrl from '../assets/ui_button/info_idle.png';
import infoPressedUrl from '../assets/ui_button/info_pressed.png';
import betPlusUrl from '../assets/ui_button/bet_plus.png';
import betMinusUrl from '../assets/ui_button/bet_minus.png';
// Per-game scene textures — Plinko / Card / Bingo painterly assets.
// Loaded into the same asset bundle as UI icons since AssetLoader
// already iterates that record without caring about logical grouping.
import lightBezelUrl from '../assets/road/light_bezel.png';
import lampLensUrl from '../assets/road/lamp_lens.png';
import { roadFriendTextures } from '../components/journey/friends';
import potSilverUrl from '../assets/road/pot_silver.png';
import bombUrl from '../assets/item/bomb.png';
import lockIconUrl from '../assets/icon/lock.png';
import potGoldUrl from '../assets/road/pot_gold.png';
import pathStripUrl from '../assets/road/path_strip.png';
import stopIdleUrl from '../assets/ui_button/stop_idle.png';
import collectIdleUrl from '../assets/ui_button/collect_idle.png';
import doneIdleUrl from '../assets/ui_button/done_idle.png';
import { BUTTON_PLATES, ROAD_GAME_ICONS, roadGamesFor } from './roadGames';
import { PATH_WORLD_OF_BIOME, ROAD_LANDMARKS, ROAD_PATHS, roadPiecesFor } from './roadArt';
import starIconUrl from '../assets/icon/star.png';
import giftIconUrl from '../assets/icon/gift.png';
import checkIconUrl from '../assets/icon/check.png';
import plinkoBallUrl from '../assets/plinko/ball.png';
import plinkoPegUrl from '../assets/plinko/peg.png';
import plinkoBinUrl from '../assets/plinko/bin.png';
import plinkoChuteUrl from '../assets/plinko/chute.png';
import cardBackUrl from '../assets/card/back_default.png';
import cardFaceTemplateUrl from '../assets/card/face_template.png';
import cardSuitClubUrl from '../assets/card/suit_club.png';
import cardSuitDiamondUrl from '../assets/card/suit_diamond.png';
import cardSuitHeartUrl from '../assets/card/suit_heart.png';
import cardSuitSpadeUrl from '../assets/card/suit_spade.png';
import bingoBallMasterUrl from '../assets/bingo/ball_master.png';
import bingoCallerCageUrl from '../assets/bingo/caller_cage.png';
import bingoCallerPlateUrl from '../assets/bingo/caller_plate.png';
import bingoCardPaperUrl from '../assets/bingo/card_paper.png';
import bingoDauberStampUrl from '../assets/bingo/dauber_stamp.png';
import bingoFreespaceUrl from '../assets/bingo/freespace.png';
// Pick-em (Hidden Treasure) chest art — asset brief pack 3 §30. The
// .webm has alpha (VP8 yuva420p) and is loaded lazily by AssetLoader
// only when ChestOpenScene actually mounts, mirroring the slot
// win-video pipeline. The {@code ?url} suffix forces Vite to emit
// a stable URL so AssetLoader's URL-keyed cache works.
import pickemChestClosedUrl from '../assets/pickem/chest_closed.png?url';
import pickemChestOpenUrl from '../assets/pickem/chest_open.png?url';
import pickemChestOpenVideoUrl from '../assets/pickem/chest_open.webm?url';
// Its HEVC-with-alpha copy, which WebKit plays in place of the WebM.
import pickemChestOpenHevcUrl from '../assets/pickem/chest_open.mov?url';
// ---- Audio assets ----------------------------------------------------------
// UI sounds
import uiClickUrl from '../assets/audio/ui/click.mp3';
import uiClickPrimaryUrl from '../assets/audio/ui/click_primary.mp3';
import uiHoverUrl from '../assets/audio/ui/hover.mp3';
import uiBackUrl from '../assets/audio/ui/back.mp3';
import uiErrorUrl from '../assets/audio/ui/error.mp3';
import uiModalOpenUrl from '../assets/audio/ui/modal_open.mp3';
import uiModalCloseUrl from '../assets/audio/ui/modal_close.mp3';
// Slot SFX
import slotSpinStartUrl from '../assets/audio/slot/spin_start.mp3';
import slotSpinLoopUrl from '../assets/audio/slot/spin_loop.mp3';
import slotReelLand1Url from '../assets/audio/slot/reel_land_1.mp3';
import slotReelLand2Url from '../assets/audio/slot/reel_land_2.mp3';
import slotReelLand3Url from '../assets/audio/slot/reel_land_3.mp3';
import slotReelLand4Url from '../assets/audio/slot/reel_land_4.mp3';
import slotReelLand5Url from '../assets/audio/slot/reel_land_5.mp3';
import slotCoinsLandSmallUrl from '../assets/audio/slot/coins_land_small.mp3';
import slotCoinsLandMediumUrl from '../assets/audio/slot/coins_land_medium.mp3';
import slotCoinsLandBigUrl from '../assets/audio/slot/coins_land_big.mp3';
import slotBonusDropUrl from '../assets/audio/slot/bonus_drop.mp3';
import slotLineMatchUrl from '../assets/audio/slot/line_match.mp3';
import slotBetChangeUrl from '../assets/audio/slot/bet_change.mp3';
// Win stings
import winSmallUrl from '../assets/audio/win/sting_small.mp3';
import winMediumUrl from '../assets/audio/win/sting_medium.mp3';
import winBigUrl from '../assets/audio/win/sting_big.mp3';
import winJackpotUrl from '../assets/audio/win/sting_jackpot.mp3';
import relicFoundUrl from '../assets/audio/bonus/relic_found.mp3';
import relicSetUrl from '../assets/audio/bonus/relic_set.mp3';
import bonusClaimUrl from '../assets/audio/bonus/claim.mp3';
// Wheel SFX
import wheelTickUrl from '../assets/audio/wheel/tick.mp3';
import wheelSlowUrl from '../assets/audio/wheel/slow.mp3';
import wheelLandUrl from '../assets/audio/wheel/land.mp3';
import wheelChipDropUrl from '../assets/audio/wheel/chip_drop.mp3';
import wheelCeremonyUrl from '../assets/audio/wheel/ceremony.mp3';
// Match flow
import matchStartUrl from '../assets/audio/match/start.mp3';
import matchEndUrl from '../assets/audio/match/end.mp3';
import castFavouriteUrl from '../assets/audio/cast/favourite.mp3';
import coinCollectUrl from '../assets/audio/coin/collect.mp3';
// Biome music — looping per-biome backing track (AudioBus.playMusic)
import musicForestKingdomUrl from '../assets/audio/music/forest_kingdom.mp3';
import musicCoveUrl from '../assets/audio/music/cove.mp3';
import musicFrontierUrl from '../assets/audio/music/frontier.mp3';
import musicFrostUrl from '../assets/audio/music/frost.mp3';
import musicNeonUrl from '../assets/audio/music/neon.mp3';
// Lobby music — generic lounge loop, plays on the world-map view
import musicLobbyUrl from '../assets/audio/music/lobby.mp3';
// Biome ambient — looping environmental bed underneath the music
import ambientForestUrl from '../assets/audio/ambient/forest.mp3';
import ambientCoveUrl from '../assets/audio/ambient/cove.mp3';
import ambientFrontierUrl from '../assets/audio/ambient/frontier.mp3';
import ambientFrostUrl from '../assets/audio/ambient/frost.mp3';
import ambientNeonUrl from '../assets/audio/ambient/neon.mp3';
// Biome audio for post-launch biomes (scaffold-managed). Placeholder
// mp3s until `./gradlew generateAudio --alias audio/music/<biome>` (and
// ambient) produce the real tracks; the import only needs the file to
// exist for the bundler.
import musicEgyptUrl from '../assets/audio/music/egypt.mp3';
import musicCandyUrl from '../assets/audio/music/candy.mp3';
import ambientEgyptUrl from '../assets/audio/ambient/egypt.mp3';
import ambientCandyUrl from '../assets/audio/ambient/candy.mp3';
// <scaffold:audio-import> — scaffoldBiome inserts new biome mp3 imports above this line
import {FOREST_THEME} from './forestTheme';
import {UNDERWATER_THEME} from './underwaterTheme';
import {FROST_THEME} from './frostTheme';
import {FRONTIER_THEME} from './frontierTheme';
import {NEON_THEME} from './neonTheme';
// Kingdom-biome themed slot variants. Each rides on the same engine
// slot-id schema as the five canonical themes; only the visible art
// changes. Win-WebMs land via the Veo pipeline (see WinAnimRegistry)
// — until those generate, each themed slot holds its static art on
// win like the engine's default behaviour.
import {ROYAL_THEME} from './royalTheme';
import {DRAGON_THEME} from './dragonTheme';
import {KNIGHTS_THEME} from './knightsTheme';
import {WIZARD_THEME} from './wizardTheme';
import {GARDENS_THEME} from './gardensTheme';
import {DUNGEONS_THEME} from './dungeonsTheme';
// <scaffold:theme-import> — scaffoldSlot inserts new theme imports here
import { SUGARPLUM_VAULT_THEME } from './sugarplum_vaultTheme';
import { PEPPERMINT_PIPEWORKS_THEME } from './peppermint_pipeworksTheme';
import { FIZZ_FACTORY_THEME } from './fizz_factoryTheme';
import { GUMMY_LAB_THEME } from './gummy_labTheme';
import { COCOA_RIVER_THEME } from './cocoa_riverTheme';
import { LOLLIPOP_LANE_THEME } from './lollipop_laneTheme';
import { SHERBET_SQUARE_THEME } from './sherbet_squareTheme';
import { EYE_OF_RA_THEME } from './eye_of_raTheme';
import { ANUBIS_THEME } from './anubisTheme';
import { BOOK_OF_DEAD_THEME } from './book_of_deadTheme';
import { CLEOPATRA_THEME } from './cleopatraTheme';
import { PHARAOHS_RICHES_THEME } from './pharaohs_richesTheme';
import { PHARAOH_TOMB_THEME } from './pharaoh_tombTheme';
import { CARAVAN_THEME } from './caravanTheme';
import {GLITCH_THEME} from './glitchTheme';
import {SKYLINE_THEME} from './skylineTheme';
import {SPEEDWAY_THEME} from './speedwayTheme';
import {CYBER_HEIST_THEME} from './cyber_heistTheme';
import {SYNTHWAVE_THEME} from './synthwaveTheme';
import {ARCADE_THEME} from './arcadeTheme';
import {HIGH_NOON_THEME} from './high_noonTheme';
import {STAMPEDE_THEME} from './stampedeTheme';
import {IRON_HORSE_THEME} from './iron_horseTheme';
import {BOUNTY_THEME} from './bountyTheme';
import {SALOON_THEME} from './saloonTheme';
import {GOLD_MINE_THEME} from './gold_mineTheme';
import {GLACIER_KEEP_THEME} from './glacier_keepTheme';
import {HOT_SPRINGS_THEME} from './hot_springsTheme';
import {YETI_THEME} from './yetiTheme';
import {AURORA_THEME} from './auroraTheme';
import {ICE_CAVERN_THEME} from './ice_cavernTheme';
import {AVALANCHE_THEME} from './avalancheTheme';
import {BURIED_TREASURE_THEME} from './buried_treasureTheme';
import {DAVY_JONES_THEME} from './davy_jonesTheme';
import {STORMY_SEAS_THEME} from './stormy_seasTheme';
import {MERMAID_COVE_THEME} from './mermaid_coveTheme';
import {MAP_FRAGMENT_THEME} from './map_fragmentTheme';
import {KRAKEN_THEME} from './krakenTheme';

export {
  FOREST_THEME,
  UNDERWATER_THEME,
  FROST_THEME,
  FRONTIER_THEME,
  NEON_THEME,
  ROYAL_THEME,
  DRAGON_THEME,
  KNIGHTS_THEME,
  WIZARD_THEME,
  GARDENS_THEME,
  DUNGEONS_THEME,
  // <scaffold:theme-export> — scaffoldSlot inserts new theme exports here
  SUGARPLUM_VAULT_THEME,
  PEPPERMINT_PIPEWORKS_THEME,
  FIZZ_FACTORY_THEME,
  GUMMY_LAB_THEME,
  COCOA_RIVER_THEME,
  LOLLIPOP_LANE_THEME,
  SHERBET_SQUARE_THEME,
  EYE_OF_RA_THEME,
  ANUBIS_THEME,
  BOOK_OF_DEAD_THEME,
  CLEOPATRA_THEME,
  PHARAOHS_RICHES_THEME,
  PHARAOH_TOMB_THEME,
  CARAVAN_THEME,
  GLITCH_THEME,
  SKYLINE_THEME,
  SPEEDWAY_THEME,
  CYBER_HEIST_THEME,
  SYNTHWAVE_THEME,
  ARCADE_THEME,
  HIGH_NOON_THEME,
  STAMPEDE_THEME,
  IRON_HORSE_THEME,
  BOUNTY_THEME,
  SALOON_THEME,
  GOLD_MINE_THEME,
  GLACIER_KEEP_THEME,
  HOT_SPRINGS_THEME,
  YETI_THEME,
  AURORA_THEME,
  ICE_CAVERN_THEME,
  AVALANCHE_THEME,
  BURIED_TREASURE_THEME,
  DAVY_JONES_THEME,
  STORMY_SEAS_THEME,
  MERMAID_COVE_THEME,
  MAP_FRAGMENT_THEME,
  KRAKEN_THEME,
};

/**
 * Win-tier type-treatment URLs (asset brief §8). Same set across all
 * themes in v1 — the treatments are theme-neutral celebrations
 * ("BIG WIN", "MEGA WIN", "JACKPOT") that read independently of the
 * environment art behind the slot. Each theme's bundle references
 * these via {@code shared:tier:*} aliases; AssetLoader.loadTheme
 * dedupes so the textures only load once.
 *
 * <p>Per-theme overrides are still possible later (e.g. neon could
 * ship a chrome BIG WIN treatment) — just override the entry in the
 * theme file's {@code assets.scene.winTiers}.
 */
export const SHARED_WIN_TIERS: Record<WinTier, ThemeAssetEntry> = {
  win:            { alias: 'shared:tier:win',            src: winUrl },
  big_win:        { alias: 'shared:tier:big_win',        src: bigWinUrl },
  mega_win:       { alias: 'shared:tier:mega_win',       src: megaWinUrl },
  super_mega_win: { alias: 'shared:tier:super_mega_win', src: superMegaWinUrl },
  jackpot:        { alias: 'shared:tier:jackpot',        src: jackpotUrl },
};

/**
 * Shared UI-icon textures used by engine widgets (BalanceDisplay,
 * IconButton, PrimaryButton). Asset brief §7 — `icon/{coin,info,spin,...}.png`.
 * Aliases match the hardcoded constants the engine widgets resolve
 * against, so registering the icons under these keys causes the
 * matching widget to swap from its emoji/text fallback to the
 * commissioned PNG sprite on first render.
 *
 * <p>Same across all themes by default; per-theme overrides are
 * possible by replacing entries on the active theme.
 */
export const SHARED_UI_ICONS: Record<string, ThemeAssetEntry> = {
  coin: { alias: 'shared:icon:coin', src: coinIconUrl },
  coin_locked: { alias: 'shared:icon:coin-locked', src: coinLockedIconUrl },
  info: { alias: 'shared:icon:info', src: infoIconUrl },
  spin: { alias: 'shared:icon:spin', src: spinIconUrl },
  // Gem-on-reel uses the same wallet-pill artwork the React BalancePill
  // renders, so the flying-gem take-win reads as "the gem you saw on
  // the reel just flew into your wallet" — no second visual identity.
  gem: { alias: 'shared:icon:gem', src: gemIconUrl },
  // The daily bonus board's gift (day 7) and tick (days done).
  gift: { alias: 'shared:icon:gift', src: giftIconUrl },
  check: { alias: 'shared:icon:check', src: checkIconUrl },
  // Commissioned button reskins — full-button textures (the asset
  // includes the label + chrome). Engine widgets check for these
  // aliases before falling back to the Graphics-pill drawing.
  spin_idle:     { alias: 'shared:button:spin_idle',     src: spinIdleUrl },
  spin_pressed:  { alias: 'shared:button:spin_pressed',  src: spinPressedUrl },
  spin_disabled: { alias: 'shared:button:spin_disabled', src: spinDisabledUrl },
  gamble_idle:   { alias: 'shared:button:gamble_idle',   src: gambleIdleUrl },
  gamble_title:  { alias: 'shared:gamble:title',         src: gambleTitleUrl },
  info_idle:     { alias: 'shared:button:info_idle',     src: infoIdleUrl },
  info_pressed:  { alias: 'shared:button:info_pressed',  src: infoPressedUrl },
  bet_plus:      { alias: 'shared:button:bet_plus',      src: betPlusUrl },
  bet_minus:     { alias: 'shared:button:bet_minus',     src: betMinusUrl },
};

/**
 * Loadout item icons — registered under {@code item:{ITEM_ID}}
 * aliases ({@code item:BOOST}, {@code item:HIT}, etc.) so the engine
 * scene's cycle-and-settle reveal can look them up by ItemId. The
 * filename (lowercased ItemId) is the source of truth; the alias is
 * generated from it. Vite's {@code import.meta.glob} discovers
 * every PNG in {@code apps/mobile/src/assets/item/} at build time —
 * the directory is empty in source control until the asset-gen CLI
 * produces the commissioned icons ({@code ./gradlew generateAssets
 * -Pargs="--asset item:*"}), at which point the new PNGs are picked
 * up automatically. Eager glob so the icons are bundled (and the
 * asset loader can preload them) instead of being code-split.
 */
const itemIconModules = import.meta.glob<{ default: string }>(
  '../assets/item/*.png',
  { eager: true },
);

export const SHARED_ITEM_ICONS: Record<string, ThemeAssetEntry> = Object.fromEntries(
  Object.entries(itemIconModules).map(([globPath, mod]) => {
    const filename = globPath.split('/').pop()!.replace(/\.png$/, '');
    const itemId = filename.toUpperCase();
    // Stage 3 — strip the '../assets/' prefix off the glob path so the
    // manifest key matches what the server-side AssetManifestService
    // writes (repo-relative under apps/mobile/src/assets/).
    const manifestPath = globPath.startsWith('../assets/')
      ? globPath.substring('../assets/'.length)
      : globPath;
    return [filename, { alias: `item:${itemId}`, src: mod.default, path: manifestPath }];
  }),
);

/**
 * Shared reel-symbol textures — slot symbols that any theme can pull
 * into its symbol pool without commissioning bespoke art per theme.
 * Currently: gem-on-reel tiers (gem_1 / gem_5 / gem_10) from
 * {@code AssetRegistry.reel:shared:gem_*}. PNGs land in
 * {@code apps/mobile/src/assets/reel/shared/} once the toolgen
 * pipeline runs; until then the glob is empty and themes that
 * reference {@code shared:reel:*} aliases fall back to the symbol's
 * emoji glyph (the AssetLoader's missing-alias path).
 *
 * <p>Glob (vs. static imports) so the asset-gen CLI can append new
 * shared symbols without an accompanying source edit — drop a PNG
 * into the folder and HMR picks it up on next build.
 */
const sharedReelModules = import.meta.glob<{ default: string }>(
  '../assets/reel/shared/*.png',
  { eager: true },
);

export const SHARED_REEL_SYMBOLS: Record<string, ThemeAssetEntry> = Object.fromEntries(
  Object.entries(sharedReelModules).map(([path, mod]) => {
    const filename = path.split('/').pop()!.replace(/\.png$/, '');
    return [filename, { alias: `shared:reel:${filename}`, src: mod.default }];
  }),
);

/** The shared reel art a slot in `world` loads: everything but the other
 *  worlds' relics (`artefact_<world>`, `_c`, `_r`, `_green`). */
export function sharedReelSymbolsFor(world: string | undefined): Record<string, ThemeAssetEntry> {
  return Object.fromEntries(
    Object.entries(SHARED_REEL_SYMBOLS).filter(([name]) => {
      const relic = /^artefact_([a-z]+)(_[a-z]+)?$/.exec(name);
      return !relic || relic[1] === world;
    }),
  );
}

/**
 * Reel-Race special symbols — the bonus collect-coin (`bonus`) and the
 * item-drop landing star (`item_drop`). During the multiplayer Reel
 * Race the server places these ids on a seat's reel grid; the slot
 * scene must resolve each id to a real {@link SlotSymbolDef} to run the
 * on-reel reveal (SlotMachine.playItemDropReveal). They ship natively
 * only in forestTheme, so they're injected into every other theme's
 * pool below — without this, picking a non-forest reel skin makes
 * {@code toEngineOutcome} fall back to a regular symbol and the awarded
 * special item never lands on the reel (regression from the reel-skin
 * selector). {@code weight: 0} keeps them out of local-mode RNG spins —
 * the server is the only thing that ever places them.
 */
const REEL_RACE_SYMBOLS: SlotSymbolDef[] = [
  { id: 'bonus', glyph: '✦', tint: 0xffc940, weight: 0, textureAlias: 'forest:bonus' },
  { id: 'item_drop', glyph: '★', tint: 0xffd86b, weight: 0, textureAlias: 'forest:item_drop' },
];

/**
 * The two gem cells (5 and 10 gems, the shared gem art), for the themes
 * made without them: the Egypt and candy worlds' slots. The server lands
 * them on every rr_v1 slot for players with reel_gems, so every theme must
 * know them; without them a gem cell would show as the theme's first
 * symbol. Weight 0 keeps them out of those slots' local spins, which never
 * had them; the themes that ship the cells keep their own entries.
 */
export const REEL_GEM_SYMBOLS: readonly SlotSymbolDef[] = [
  { id: 'gem_t5', label: 'Gems', glyph: '💎', tint: 0x3ea7e8, weight: 0, gemValue: 5, textureAlias: 'shared:reel:gem_5' },
  { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
];

/**
 * Textures for {@link REEL_RACE_SYMBOLS}, reusing forest's commissioned
 * art (aliases `forest:bonus` / `forest:item_drop`). Merged into every
 * theme's slot bundle so the sprite — not just the glyph fallback —
 * renders on any reel skin.
 */
const REEL_RACE_TEXTURES: Record<string, ThemeAssetEntry> = {
  bonus: { alias: 'forest:bonus', src: reelRaceBonusUrl },
  item_drop: { alias: 'forest:item_drop', src: reelRaceItemDropUrl },
};

/** The bezel each side light and each Lamp Run prize sits in, and the
 *  ring's other painted pieces (BoardRing's RingArt). */
const ROAD_LIGHTS: Record<string, ThemeAssetEntry> = {
  light_bezel: { alias: 'road:light_bezel', src: lightBezelUrl, path: 'road/light_bezel.png' },
  lamp_lens: { alias: 'road:lamp_lens', src: lampLensUrl, path: 'road/lamp_lens.png' },
  pot_silver: { alias: 'road:pot_silver', src: potSilverUrl, path: 'road/pot_silver.png' },
  bomb: { alias: 'road:bomb', src: bombUrl, path: 'item/bomb.png' },
  lock: { alias: 'road:lock', src: lockIconUrl, path: 'icon/lock.png' },
  // The panel's pieces, drawn on the canvas.
  pot_gold: { alias: 'road:pot_gold', src: potGoldUrl, path: 'road/pot_gold.png' },
  path_strip: { alias: 'road:path_strip', src: pathStripUrl, path: 'road/path_strip.png' },
  // The Wheel Run's stars on the panel and its dark lamps (a picture: a
  // star glyph draws as a box on some phones).
  star: { alias: 'road:star', src: starIconUrl, path: 'icon/star.png' },
};

/**
 * The Royal Road's own sounds (assets/audio/road), as `road:<name>`; the
 * engine's ROAD_SOUNDS says which plays when. The coin rain loop waits for
 * a looping SFX channel.
 */
export const ROAD_AUDIO: Record<string, ThemeAssetEntry> = Object.fromEntries(
  Object.entries(
    import.meta.glob<{ default: string }>(['../assets/audio/road/*.mp3', '!**/coin_rain_loop.mp3'], { eager: true }),
  ).map(([file, mod]) => {
    const name = file.slice(file.lastIndexOf('/') + 1, -'.mp3'.length);
    return [`road_${name}`, { alias: `road:${name}`, src: mod.default, path: `audio/road/${name}.mp3` }];
  }),
);

/**
 * The faces SlotHudComponent puts over SPIN during a run, made from the
 * SPIN art.
 */
const ROAD_BUTTONS: Record<string, ThemeAssetEntry> = {
  stop_idle:    { alias: 'shared:button:stop_idle',    src: stopIdleUrl },
  collect_idle: { alias: 'shared:button:collect_idle', src: collectIdleUrl },
  done_idle:    { alias: 'shared:button:done_idle',    src: doneIdleUrl },
};

/**
 * Hold and spin's painted pieces on every slot, loaded with its reels (the
 * bonus can come on the first spin): the brass bezels and lit lenses of
 * its spins-left lamps and the plates its COLLECT button is painted on. Its locked-cell
 * tile is a shared reel piece (reel/shared/hns_lock_tile.png). Road slots
 * load the same files with their road art; each file is fetched once.
 */
const HOLD_AND_SPIN_ART: Record<string, ThemeAssetEntry> = {
  hns_bezel: ROAD_LIGHTS.light_bezel!,
  hns_lens: ROAD_LIGHTS.lamp_lens!,
  ...BUTTON_PLATES,
};

/**
 * The Sweet Trail's road on slots without Royal Road: the strip the road
 * panel rolls (its prizes sit in the bezels above). The shared cobbles,
 * and the slot's world's own surface where it has one.
 */
function trailArtFor(biome: BiomeId | undefined): Record<string, ThemeAssetEntry> {
  const world = biome ? PATH_WORLD_OF_BIOME[biome] : undefined;
  const own = world ? ROAD_PATHS[world] : undefined;
  return { trail_path: ROAD_LIGHTS.path_strip!, ...(own ? { trail_path_own: own } : {}) };
}

/**
 * Per-game scene-chrome textures — Plinko / Card / Bingo painterly
 * assets. Engine widgets check for these aliases before falling back
 * to their drawn-Graphics renders. Attached as the theme's
 * {@code gameTextures} below, which load with the full theme.
 */
export const SHARED_GAME_TEXTURES: Record<string, ThemeAssetEntry> = {
  // Plinko (asset brief §11)
  plinko_ball:  { alias: 'plinko:ball',  src: plinkoBallUrl  },
  plinko_peg:   { alias: 'plinko:peg',   src: plinkoPegUrl   },
  plinko_bin:   { alias: 'plinko:bin',   src: plinkoBinUrl   },
  plinko_chute: { alias: 'plinko:chute', src: plinkoChuteUrl },
  // Card / Higher-Lower (asset brief §12)
  card_back:    { alias: 'card:back',    src: cardBackUrl },
  card_face:    { alias: 'card:face',    src: cardFaceTemplateUrl },
  card_gamble_red:   { alias: 'card:gamble:red',   src: gambleCardRedUrl },
  card_gamble_black: { alias: 'card:gamble:black', src: gambleCardBlackUrl },
  card_club:    { alias: 'card:club',    src: cardSuitClubUrl },
  card_diamond: { alias: 'card:diamond', src: cardSuitDiamondUrl },
  card_heart:   { alias: 'card:heart',   src: cardSuitHeartUrl },
  card_spade:   { alias: 'card:spade',   src: cardSuitSpadeUrl },
  // Bingo (asset brief §10)
  bingo_ball:        { alias: 'bingo:ball',         src: bingoBallMasterUrl },
  bingo_caller_cage: { alias: 'bingo:caller_cage',  src: bingoCallerCageUrl },
  bingo_caller_plate:{ alias: 'bingo:caller_plate', src: bingoCallerPlateUrl },
  bingo_card_paper:  { alias: 'bingo:card_paper',   src: bingoCardPaperUrl },
  bingo_dauber:      { alias: 'bingo:dauber',       src: bingoDauberStampUrl },
  bingo_freespace:   { alias: 'bingo:freespace',    src: bingoFreespaceUrl },
};

/**
 * Pick-em (Hidden Treasure) chest art — asset brief pack 3 §30.
 * Shared across themes in v1; the chest is theme-neutral painterly
 * oak-and-brass. Lives on its own bundle (not folded into uiIcons)
 * because the .webm needs the AssetLoader's video branch — the loader
 * sniffs the URL extension to decide texture vs lazy-loaded video.
 *
 * <p>Aliases are consumed by
 * {@link ../engine/widgets/chest/ChestWidget ChestWidget}.
 */
export const SHARED_PICKEM_ART: Record<string, ThemeAssetEntry> = {
  chest_closed:     { alias: 'pickem:chest_closed',     src: pickemChestClosedUrl },
  chest_open:       { alias: 'pickem:chest_open',       src: pickemChestOpenUrl },
  chest_open_video: { alias: 'pickem:chest_open_video', src: pickemChestOpenVideoUrl, hevc: { src: pickemChestOpenHevcUrl } },
};

/**
 * Shared audio bundle — ElevenLabs-generated mp3s (2026-06-04, per
 * AudioRegistry.java in goodtime-server) mapped to the AudioBus
 * aliases engine widgets + view code call into. Without this map the
 * AudioBus.loadTheme early-returns (no audio registered) and every
 * {@code AudioBus.play(alias)} call no-ops.
 *
 * <p>Per-biome music + ambient are registered alongside the SFX so
 * any theme that sets {@link Theme#musicAlias} / {@link Theme#ambientAlias}
 * can refer to them. The mapping from theme → biome alias is set in
 * the forEach patch loop below, keyed by {@code theme.id}.
 */
export const SHARED_AUDIO: Record<string, ThemeAssetEntry> = {
  // UI
  ui_click:         { alias: 'ui:click',          src: uiClickUrl,           path: 'audio/ui/click.mp3' },
  ui_click_primary: { alias: 'ui:click_primary',  src: uiClickPrimaryUrl,    path: 'audio/ui/click_primary.mp3' },
  ui_hover:         { alias: 'ui:hover',          src: uiHoverUrl,           path: 'audio/ui/hover.mp3' },
  ui_back:          { alias: 'ui:back',           src: uiBackUrl,            path: 'audio/ui/back.mp3' },
  ui_error:         { alias: 'ui:error',          src: uiErrorUrl,           path: 'audio/ui/error.mp3' },
  ui_modal_open:    { alias: 'ui:modal_open',     src: uiModalOpenUrl,       path: 'audio/ui/modal_open.mp3' },
  ui_modal_close:   { alias: 'ui:modal_close',    src: uiModalCloseUrl,      path: 'audio/ui/modal_close.mp3' },
  // Slot
  // The reels (engine REEL_SOUNDS): new files under new names, so the CDN's
  // copies of the old reel sounds can never be served in their place.
  slot_spin_start:  { alias: 'slot:spin_start',   src: slotSpinStartUrl,     path: 'audio/slot/spin_start.mp3' },
  slot_spin_loop:   { alias: 'slot:spin_loop',    src: slotSpinLoopUrl,      path: 'audio/slot/spin_loop.mp3' },
  slot_reel_land_1: { alias: 'slot:reel_land_1',  src: slotReelLand1Url,     path: 'audio/slot/reel_land_1.mp3' },
  slot_reel_land_2: { alias: 'slot:reel_land_2',  src: slotReelLand2Url,     path: 'audio/slot/reel_land_2.mp3' },
  slot_reel_land_3: { alias: 'slot:reel_land_3',  src: slotReelLand3Url,     path: 'audio/slot/reel_land_3.mp3' },
  slot_reel_land_4: { alias: 'slot:reel_land_4',  src: slotReelLand4Url,     path: 'audio/slot/reel_land_4.mp3' },
  slot_reel_land_5: { alias: 'slot:reel_land_5',  src: slotReelLand5Url,     path: 'audio/slot/reel_land_5.mp3' },
  // A winning spin's last reel lands with coins, by the size of the win.
  slot_coins_land_small:  { alias: 'slot:coins_land_small',  src: slotCoinsLandSmallUrl,  path: 'audio/slot/coins_land_small.mp3' },
  slot_coins_land_medium: { alias: 'slot:coins_land_medium', src: slotCoinsLandMediumUrl, path: 'audio/slot/coins_land_medium.mp3' },
  slot_coins_land_big:    { alias: 'slot:coins_land_big',    src: slotCoinsLandBigUrl,    path: 'audio/slot/coins_land_big.mp3' },
  slot_bonus_drop:  { alias: 'slot:bonus_drop',   src: slotBonusDropUrl,     path: 'audio/slot/bonus_drop.mp3' },
  slot_line_match:  { alias: 'slot:line_match',   src: slotLineMatchUrl,     path: 'audio/slot/line_match.mp3' },
  slot_bet_change:  { alias: 'slot:bet_change',   src: slotBetChangeUrl,     path: 'audio/slot/bet_change.mp3' },
  // Win stings
  win_small:        { alias: 'win:sting_small',   src: winSmallUrl,          path: 'audio/win/sting_small.mp3' },
  win_medium:       { alias: 'win:sting_medium',  src: winMediumUrl,         path: 'audio/win/sting_medium.mp3' },
  win_big:          { alias: 'win:sting_big',     src: winBigUrl,            path: 'audio/win/sting_big.mp3' },
  win_jackpot:      { alias: 'win:sting_jackpot', src: winJackpotUrl,        path: 'audio/win/sting_jackpot.mp3' },
  bonus_claim:      { alias: 'bonus:claim',       src: bonusClaimUrl,        path: 'audio/bonus/claim.mp3' },
  // Reel collectibles: a relic found, and a set of three completed.
  relic_found:      { alias: 'bonus:relic_found', src: relicFoundUrl,        path: 'audio/bonus/relic_found.mp3' },
  relic_set:        { alias: 'bonus:relic_set',   src: relicSetUrl,          path: 'audio/bonus/relic_set.mp3' },
  // Wheel
  wheel_tick:       { alias: 'wheel:tick',        src: wheelTickUrl,         path: 'audio/wheel/tick.mp3' },
  wheel_slow:       { alias: 'wheel:slow',        src: wheelSlowUrl,         path: 'audio/wheel/slow.mp3' },
  wheel_land:       { alias: 'wheel:land',        src: wheelLandUrl,         path: 'audio/wheel/land.mp3' },
  wheel_chip_drop:  { alias: 'wheel:chip_drop',   src: wheelChipDropUrl,     path: 'audio/wheel/chip_drop.mp3' },
  wheel_ceremony:   { alias: 'wheel:ceremony',    src: wheelCeremonyUrl,     path: 'audio/wheel/ceremony.mp3' },
  // Match flow
  match_start:      { alias: 'match:start',       src: matchStartUrl,        path: 'audio/match/start.mp3' },
  match_end:        { alias: 'match:end',         src: matchEndUrl,          path: 'audio/match/end.mp3' },
  cast_favourite:   { alias: 'cast:favourite',    src: castFavouriteUrl,     path: 'audio/cast/favourite.mp3' },
  coin_collect:     { alias: 'coin:collect',      src: coinCollectUrl,       path: 'audio/coin/collect.mp3' },
  // Note: chip:drop alias used by some legacy code paths — alias to the
  // wheel chip-drop sample so existing call sites just work. Same path
  // as wheel_chip_drop above — the manifest is keyed on path, so the
  // CDN URL resolves to the same byte stream.
  chip_drop:        { alias: 'chip:drop',         src: wheelChipDropUrl,     path: 'audio/wheel/chip_drop.mp3' },
};

/**
 * Per-biome music + ambient entries. NOT included in {@link SHARED_AUDIO}
 * so themes can lazy-load only the biome they need — installing forest
 * doesn't drag 22MB of neon/frost/frontier/cove music into the first
 * fetch. The forEach patch below appends each theme's biome entries
 * to its own {@code assets.audio} map keyed by {@code THEME_BIOME_AUDIO}.
 *
 * <p>Stage 1 of the install-size project: pre-CDN, all packs still ship
 * in the IPA/APK but the lazy-load architecture is the foundation
 * Stage 3 (CDN-backed packs) builds on.
 */
const BIOME_MUSIC_ENTRIES: Record<string, ThemeAssetEntry> = {
  'music:forest_kingdom': { alias: 'music:forest_kingdom', src: musicForestKingdomUrl, path: 'audio/music/forest_kingdom.mp3' },
  'music:cove':           { alias: 'music:cove',           src: musicCoveUrl,           path: 'audio/music/cove.mp3' },
  'music:frontier':       { alias: 'music:frontier',       src: musicFrontierUrl,       path: 'audio/music/frontier.mp3' },
  'music:frost':          { alias: 'music:frost',          src: musicFrostUrl,          path: 'audio/music/frost.mp3' },
  'music:neon':           { alias: 'music:neon',           src: musicNeonUrl,           path: 'audio/music/neon.mp3' },
  'music:egypt':          { alias: 'music:egypt',          src: musicEgyptUrl,          path: 'audio/music/egypt.mp3' },
  'music:candy':          { alias: 'music:candy',          src: musicCandyUrl,          path: 'audio/music/candy.mp3' },
  // <scaffold:biome-music-entry> — scaffoldBiome inserts new biome music entries above this line
};

const BIOME_AMBIENT_ENTRIES: Record<string, ThemeAssetEntry> = {
  'ambient:forest':       { alias: 'ambient:forest',       src: ambientForestUrl,       path: 'audio/ambient/forest.mp3' },
  'ambient:cove':         { alias: 'ambient:cove',         src: ambientCoveUrl,         path: 'audio/ambient/cove.mp3' },
  'ambient:frontier':     { alias: 'ambient:frontier',     src: ambientFrontierUrl,     path: 'audio/ambient/frontier.mp3' },
  'ambient:frost':        { alias: 'ambient:frost',        src: ambientFrostUrl,        path: 'audio/ambient/frost.mp3' },
  'ambient:neon':         { alias: 'ambient:neon',         src: ambientNeonUrl,         path: 'audio/ambient/neon.mp3' },
  'ambient:egypt':        { alias: 'ambient:egypt',        src: ambientEgyptUrl,        path: 'audio/ambient/egypt.mp3' },
  'ambient:candy':        { alias: 'ambient:candy',        src: ambientCandyUrl,        path: 'audio/ambient/candy.mp3' },
  // <scaffold:biome-ambient-entry> — scaffoldBiome inserts new biome ambient entries above this line
};

/** Lobby music URL — exported so WorldMapV2 can register it via
 *  {@link AudioBus#loadEntry} on mount (it isn't tied to any theme). */
export const LOBBY_MUSIC = {
  alias: 'music:lobby',
  src: musicLobbyUrl,
  path: 'audio/music/lobby.mp3',
} as const;

/**
 * Theme-id → biome music+ambient alias mapping. Kingdom-themed slot
 * variants (e.g. royal/dragon/knights all use forest_kingdom music)
 * inherit their parent biome's track.
 *
 * <p>Setting a theme.id in this map opts that theme into looping
 * music + ambient when its scene mounts. Themes not listed here ship
 * silent (musicAlias/ambientAlias stay undefined).
 */
const THEME_BIOME_AUDIO: Record<string, { musicAlias: string; ambientAlias: string }> = {
  // Forest (Whispering Woods / Kingdom variants)
  forest:            { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  royal:             { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  dragon:            { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  knights:           { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  wizard:            { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  gardens:           { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  dungeons:          { musicAlias: 'music:forest_kingdom', ambientAlias: 'ambient:forest' },
  // Cove (Coral Reef / underwater + treasure variants)
  underwater:        { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  buried_treasure:   { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  davy_jones:        { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  stormy_seas:       { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  mermaid_cove:      { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  map_fragment:      { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  kraken:            { musicAlias: 'music:cove',           ambientAlias: 'ambient:cove' },
  // Frost (Aurora Hall + ice variants)
  frost:             { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  glacier_keep:      { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  hot_springs:       { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  yeti:              { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  aurora:            { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  ice_cavern:        { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  avalanche:         { musicAlias: 'music:frost',          ambientAlias: 'ambient:frost' },
  // Frontier (Sunset Saloon + western variants)
  frontier:          { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  high_noon:         { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  stampede:          { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  iron_horse:        { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  bounty:            { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  saloon:            { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  gold_mine:         { musicAlias: 'music:frontier',       ambientAlias: 'ambient:frontier' },
  // Neon (Skyline Vault + cyber / arcade variants)
  neon:              { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  glitch:            { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  skyline:           { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  speedway:          { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  cyber_heist:       { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  synthwave:         { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  arcade:            { musicAlias: 'music:neon',           ambientAlias: 'ambient:neon' },
  // Egypt (Sunscar Sands)
  caravan:           { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  pharaoh_tomb:      { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  pharaohs_riches:   { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  cleopatra:         { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  book_of_dead:      { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  anubis:            { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  eye_of_ra:         { musicAlias: 'music:egypt',          ambientAlias: 'ambient:egypt' },
  // Candy (Wonderfizz Works)
  sherbet_square:       { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  lollipop_lane:        { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  cocoa_river:          { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  gummy_lab:            { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  fizz_factory:         { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  peppermint_pipeworks: { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  sugarplum_vault:      { musicAlias: 'music:candy',       ambientAlias: 'ambient:candy' },
  // <scaffold:theme-biome-audio> — scaffoldBiome inserts new biome's slot-theme rows above this line
};

/**
 * Map a theme's biome-music alias back to a {@link BiomeId}. The
 * {@link THEME_BIOME_AUDIO} table already groups every theme into its
 * biome via the music track it plays; this re-derives the biome id
 * from that single source rather than maintaining a second theme→biome
 * table that could drift. */
const MUSIC_ALIAS_TO_BIOME: Record<string, BiomeId> = {
  'music:forest_kingdom': 'kingdom',
  'music:cove': 'cove',
  'music:frost': 'frost',
  'music:frontier': 'frontier',
  'music:neon': 'neon',
  'music:egypt': 'egypt',
  'music:candy': 'candy',
  // <scaffold:music-alias-biome> — scaffoldBiome inserts new biome alias→id rows above this line
};

/**
 * One reel-symbol def per biome artefact (2026-06-06 retention
 * collectibles). Built from the shared {@link ARTEFACTS} catalog so
 * the names / glyphs / count stay in lockstep with the collection UI
 * and the engine's total. Each is injected into every theme in its
 * biome by the patch loop below — so any slot in Greendale can drop
 * Excalibur, any slot in the Cove can drop the Trident, etc.
 *
 * <p>Ultra-low weight (0.02) — rarer than gem_t10 (0.05) — so an
 * uncover is a genuine once-in-a-long-while event. The texture alias
 * resolves through the same {@code reel/shared/*.png} glob as gems
 * (SHARED_REEL_SYMBOLS), so dropping {@code artefact_<biome>.png} into
 * that folder lights them up; until the art lands they fall back to
 * the emoji glyph. The {@code artefactId} field (= biome id) is what
 * settlement keys off — the {@code id} string is cosmetic. */
const BIOME_ARTEFACT_SYMBOLS: Record<BiomeId, SlotSymbolDef> = Object.fromEntries(
  ARTEFACTS.map((a) => [
    a.id,
    {
      id: `artefact_${a.id}`,
      label: a.name,
      glyph: a.glyph,
      tint: 0xf5d67a, // legendary gold — uniform across biomes
      weight: 0.02,
      artefactId: a.id,
      textureAlias: `shared:reel:artefact_${a.id}`,
    } satisfies SlotSymbolDef,
  ]),
) as Record<BiomeId, SlotSymbolDef>;

// Attach the shared win-tier map + UI icons + audio bundle onto every
// commissioned theme. Done here (not in each theme file) because the
// same set is reused across all five — and AssetLoader.loadTheme +
// AudioBus.loadTheme dedupe so we don't pay for duplicate loads.
// Engine-bundled themes (default/midnight/sunset) stay unmodified
// since they don't ship sprite or audio assets.
// Every commissioned theme (canonical biomes + Kingdom-biome themed
// slot variants) gets the shared UI icons, win-tier treatments,
// pickem chest art, and audio bundle patched on at module load.
// Forgetting to include a theme here leaves its slot scene with the
// engine's drawn-Graphics fallbacks — most visibly, a flat solid
// circle instead of the chunky pink SPIN button.
[
  FOREST_THEME,
  UNDERWATER_THEME,
  FROST_THEME,
  FRONTIER_THEME,
  NEON_THEME,
  ROYAL_THEME,
  DRAGON_THEME,
  KNIGHTS_THEME,
  WIZARD_THEME,
  GARDENS_THEME,
  DUNGEONS_THEME,
  // <scaffold:theme-patch> — scaffoldSlot inserts new themes here
  SUGARPLUM_VAULT_THEME,
  PEPPERMINT_PIPEWORKS_THEME,
  FIZZ_FACTORY_THEME,
  GUMMY_LAB_THEME,
  COCOA_RIVER_THEME,
  LOLLIPOP_LANE_THEME,
  SHERBET_SQUARE_THEME,
  EYE_OF_RA_THEME,
  ANUBIS_THEME,
  BOOK_OF_DEAD_THEME,
  CLEOPATRA_THEME,
  PHARAOHS_RICHES_THEME,
  PHARAOH_TOMB_THEME,
  CARAVAN_THEME,
  GLITCH_THEME,
  SKYLINE_THEME,
  SPEEDWAY_THEME,
  CYBER_HEIST_THEME,
  SYNTHWAVE_THEME,
  ARCADE_THEME,
  HIGH_NOON_THEME,
  STAMPEDE_THEME,
  IRON_HORSE_THEME,
  BOUNTY_THEME,
  SALOON_THEME,
  GOLD_MINE_THEME,
  GLACIER_KEEP_THEME,
  HOT_SPRINGS_THEME,
  YETI_THEME,
  AURORA_THEME,
  ICE_CAVERN_THEME,
  AVALANCHE_THEME,
  BURIED_TREASURE_THEME,
  DAVY_JONES_THEME,
  STORMY_SEAS_THEME,
  MERMAID_COVE_THEME,
  MAP_FRAGMENT_THEME,
  KRAKEN_THEME,
].forEach(
  (theme) => {
    if (!theme.assets) {
      // (Theme.assets is optional, but every commissioned theme
      // declares assets in its source — this branch is defensive.)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (theme as any).assets = {};
    }
    if (!theme.assets!.scene) {
      theme.assets!.scene = {};
    }
    theme.assets!.scene.winTiers = SHARED_WIN_TIERS;
    // Shared reel symbols (gem_1/5/10) — merge into every theme's
    // slot bundle so any symbols array referencing
    // {@code shared:reel:gem_*} resolves to the same PNG everywhere.
    // Theme's own per-id slot entries (cherry/lemon/…) sit alongside;
    // the engine reads each entry by {@code entry.alias} so the key
    // collision space (slot-id vs. gem_N) is purely organisational.
    // Relics: a slot loads only its own world's three (every world's are in
    // the shared folder; a slot only ever shows its own).
    const relicWorld =
      BIOME_BY_THEME_ID[theme.id] ??
      MUSIC_ALIAS_TO_BIOME[THEME_BIOME_AUDIO[theme.id]?.musicAlias ?? ''];
    theme.assets!.slot = {
      ...(theme.assets!.slot ?? {}),
      ...sharedReelSymbolsFor(relicWorld),
      ...REEL_RACE_TEXTURES,
      ...HOLD_AND_SPIN_ART,
      ...trailArtFor(relicWorld),
    };
    // Inject this biome's artefact into the theme's symbol pool so it
    // can land on the reels (2026-06-06). Done centrally here — not in
    // the 35 theme files — because the artefact is biome-specific:
    // every Greendale theme gets Excalibur, every Cove theme the
    // Trident, etc. The biome is derived from the shared slot registry
    // ({@link BIOME_BY_THEME_ID}, built from TYPES_BY_BIOME) so EVERY
    // biome resolves — including ones with no commissioned music yet
    // (egypt, candy) — instead of the audio table, which only lists
    // themes that ship a music track and so silently dropped the
    // artefact for those biomes. The audio chain stays as a fallback for
    // the canonical biome-hero themes (forest/underwater/frost/frontier/
    // neon) that are generic 'slot' nodes absent from the slot registry.
    // Idempotent against hot-reload via the {@code artefactId} guard so
    // re-running the module doesn't stack duplicate artefacts.
    const biomeId =
      BIOME_BY_THEME_ID[theme.id] ??
      MUSIC_ALIAS_TO_BIOME[THEME_BIOME_AUDIO[theme.id]?.musicAlias ?? ''];
    if (biomeId) {
      const artefactSymbol = BIOME_ARTEFACT_SYMBOLS[biomeId];
      const alreadyHasArtefact = theme.slot.symbols.some(
        (s) => s.artefactId !== undefined && s.artefactId !== '',
      );
      if (artefactSymbol && !alreadyHasArtefact) {
        // theme.slot.symbols is typed readonly; replace with a fresh
        // array (the runtime value is a plain mutable array, but the
        // reassign keeps us honest about not mutating in place). Cast
        // through unknown because SlotThemeData has other readonly
        // fields the narrow target shape doesn't model.
        (theme.slot as unknown as { symbols: SlotSymbolDef[] }).symbols = [
          ...theme.slot.symbols,
          artefactSymbol,
        ];
      }
    }
    // Reel-Race special symbols (bonus + item_drop) — inject any the
    // theme doesn't already ship so a server-placed cell resolves to a
    // real SlotSymbolDef on every reel skin, not just forest. Mirrors the
    // artefact injection above; idempotent via the id guard (forest ships
    // both natively, so it's skipped there).
    const missingReelRace = REEL_RACE_SYMBOLS.filter(
      (sym) => !theme.slot.symbols.some((s) => s.id === sym.id),
    );
    if (missingReelRace.length > 0) {
      (theme.slot as unknown as { symbols: SlotSymbolDef[] }).symbols = [
        ...theme.slot.symbols,
        ...missingReelRace,
      ];
    }
    // The gem cells, for a theme made without them (see REEL_GEM_SYMBOLS).
    const missingGems = REEL_GEM_SYMBOLS.filter((sym) => !theme.slot.symbols.some((s) => s.id === sym.id));
    if (missingGems.length > 0) {
      (theme.slot as unknown as { symbols: SlotSymbolDef[] }).symbols = [...theme.slot.symbols, ...missingGems];
    }
    // HUD chrome is part of a slot's first-spin preload; other games'
    // textures and the loadout item icons load with the full theme
    // (AssetLoader.loadThemeEssentials vs loadTheme). Widgets resolve
    // both by alias, so the split only changes loading order.
    theme.assets!.scene.uiIcons = { ...SHARED_UI_ICONS };
    // The background's life (butterflies, birds, drifting light), for the
    // themes it suits; its creatures load with the rest of the theme.
    const life = AMBIENT_LIFE[theme.id];
    if (life) theme.ambientLife = life;
    theme.assets!.scene.gameTextures = {
      ...SHARED_GAME_TEXTURES,
      // Empty until the asset-gen CLI produces the commissioned PNGs;
      // until then the cycle-and-settle reveal falls back to the
      // star item_drop symbol for missing item textures.
      ...SHARED_ITEM_ICONS,
      ...(life ? AMBIENT_ART : {}),
    };
    // Pick-em (Hidden Treasure) chest art lives on its own bucket —
    // AssetLoader sniffs the .webm URL and routes it through the
    // lazy-video path so the chest scene's preload doesn't block on a
    // 2MB animation that only fires once the player picks.
    theme.assets!.scene.pickem = SHARED_PICKEM_ART;
    // Biome music + ambient — looked up by theme.id; unmatched themes
    // ship silent (musicAlias / ambientAlias stay undefined). Scenes
    // that load the theme then call AudioBus.playMusic / playAmbient
    // automatically. Music+ambient are added to the theme's OWN audio
    // bundle (not SHARED_AUDIO) so a theme load only fetches its biome's
    // ~4MB track, not all 22MB of music across every biome.
    const biomeAudio = THEME_BIOME_AUDIO[theme.id];
    const themeAudio: Record<string, ThemeAssetEntry> = { ...SHARED_AUDIO };
    if (biomeAudio) {
      theme.musicAlias = biomeAudio.musicAlias;
      theme.ambientAlias = biomeAudio.ambientAlias;
      const musicEntry = BIOME_MUSIC_ENTRIES[biomeAudio.musicAlias];
      const ambientEntry = BIOME_AMBIENT_ENTRIES[biomeAudio.ambientAlias];
      if (musicEntry) themeAudio.biome_music = musicEntry;
      if (ambientEntry) themeAudio.biome_ambient = ambientEntry;
    }
    theme.assets!.audio = themeAudio;
    // Royal Road art and sounds: fetched by the slot only when the road (or
    // the Wheel Run) is on for the player, never with the theme. A Wheel Run
    // slot has its world road's ring, hub, life token and path.
    const pieces = roadPiecesFor(theme.id);
    if (pieces) {
      const { ring, life, hub, path } = pieces;
      theme.assets!.road = {
        textures: {
          ...ROAD_LIGHTS,
          road_ring: ring,
          ...(life ? { road_life: life } : {}),
          ...(hub ? { road_hub: hub } : {}),
          ...(path ? { road_path: path } : {}),
          ...ROAD_BUTTONS,
          ...BUTTON_PLATES,
          ...ROAD_GAME_ICONS,
          ...ROAD_LANDMARKS[theme.id],
          ...roadFriendTextures(theme.id),
        },
        audio: ROAD_AUDIO,
        games: roadGamesFor(theme.id),
      };
    }
  },
);

/**
 * Replacement for {@link createDefaultThemeRegistry} that registers
 * all five commissioned themes alongside the engine's hand-crafted
 * defaults. Forest is the v1 default (matches the home wheel room).
 *
 * <p>Existing engine themes (default/midnight/sunset) stay registered
 * for now — they're the "base art" reference per the master plan, and
 * the shop's premium-theme machinery already keys off their ids.
 * Production may eventually retire them; right now they coexist.
 */
export function createGoodtimeThemeRegistry(): ThemeRegistry {
  return new ThemeRegistry(
    [
      FOREST_THEME,
      UNDERWATER_THEME,
      FROST_THEME,
      FRONTIER_THEME,
      NEON_THEME,
      ROYAL_THEME,
      DRAGON_THEME,
      KNIGHTS_THEME,
      WIZARD_THEME,
      GARDENS_THEME,
      DUNGEONS_THEME,
      // <scaffold:theme-registry> — scaffoldSlot inserts new themes here
  SUGARPLUM_VAULT_THEME,
  PEPPERMINT_PIPEWORKS_THEME,
  FIZZ_FACTORY_THEME,
  GUMMY_LAB_THEME,
  COCOA_RIVER_THEME,
  LOLLIPOP_LANE_THEME,
  SHERBET_SQUARE_THEME,
  EYE_OF_RA_THEME,
  ANUBIS_THEME,
  BOOK_OF_DEAD_THEME,
  CLEOPATRA_THEME,
  PHARAOHS_RICHES_THEME,
  PHARAOH_TOMB_THEME,
  CARAVAN_THEME,
  GLITCH_THEME,
  SKYLINE_THEME,
  SPEEDWAY_THEME,
  CYBER_HEIST_THEME,
  SYNTHWAVE_THEME,
  ARCADE_THEME,
  HIGH_NOON_THEME,
  STAMPEDE_THEME,
  IRON_HORSE_THEME,
  BOUNTY_THEME,
  SALOON_THEME,
  GOLD_MINE_THEME,
  GLACIER_KEEP_THEME,
  HOT_SPRINGS_THEME,
  YETI_THEME,
  AURORA_THEME,
  ICE_CAVERN_THEME,
  AVALANCHE_THEME,
  BURIED_TREASURE_THEME,
  DAVY_JONES_THEME,
  STORMY_SEAS_THEME,
  MERMAID_COVE_THEME,
  MAP_FRAGMENT_THEME,
  KRAKEN_THEME,
      DEFAULT_THEME,
      MIDNIGHT_THEME,
      SUNSET_THEME,
    ],
    FOREST_THEME.id,
  );
}
